import { apiClient } from './client'
import type {
  CreateProjectRequest,
  MyProjectsListItem,
  PageResponse,
  ProjectDetail,
  ProjectSummary,
  SlugAvailability,
  UpdateProjectRequest,
} from './types'

export const listMyProjects = async (
  page: number = 0,
  size: number = 20
): Promise<PageResponse<ProjectSummary>> => {
  const response = await apiClient.get<PageResponse<ProjectSummary>>('/api/v1/projects', {
    params: { page, size },
  })
  return response.data
}

export const createProject = async (data: CreateProjectRequest): Promise<ProjectSummary> => {
  const response = await apiClient.post<ProjectSummary>('/api/v1/projects', data)
  return response.data
}

export const getProject = async (id: string): Promise<ProjectDetail> => {
  const response = await apiClient.get<ProjectDetail>(`/api/v1/projects/${id}`)
  return response.data
}

export const updateProject = async (
  id: string,
  data: UpdateProjectRequest
): Promise<ProjectSummary> => {
  const response = await apiClient.patch<ProjectSummary>(`/api/v1/projects/${id}`, data)
  return response.data
}

export const deleteProject = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/v1/projects/${id}`)
}

/** Phase 6: canonical-URL resolver. Anonymous-readable per project visibility. */
export const getProjectByHandleAndSlug = async (
  handle: string,
  slug: string
): Promise<ProjectDetail> => {
  const response = await apiClient.get<ProjectDetail>(
    `/api/v1/projects/by-handle-slug/${encodeURIComponent(handle)}/${encodeURIComponent(slug)}`
  )
  return response.data
}

export const checkSlugAvailability = async (
  slug: string,
  excludeProjectId?: string
): Promise<SlugAvailability> => {
  const response = await apiClient.get<SlugAvailability>('/api/v1/me/projects/slug-availability', {
    params: excludeProjectId ? { slug, excludeProjectId } : { slug },
  })
  return response.data
}

export const fetchMyProjectsForPicker = async (): Promise<MyProjectsListItem[]> => {
  const response = await apiClient.get<MyProjectsListItem[]>('/api/v1/me/projects/picker-list')
  return response.data
}

/**
 * Phase 6: insert a Project reference at the root of `masterProjectId`'s tree. Used by the
 * browse-side "Reference this in one of my projects" flow. Backend enforces source-must-be-PUBLIC
 * for cross-author refs; own-author always allowed.
 */
export const insertProjectReferenceAtRoot = async (
  masterProjectId: string,
  sourceProjectId: string
): Promise<void> => {
  await apiClient.post(`/api/v1/projects/${masterProjectId}/insert-project-reference-at-root`, {
    sourceProjectId,
  })
}
